<template>
  <div :ref="getId" class="relative">
    <p aria-hidden="true" class="flex flex-wrap gap-2 text-sm" :class="this.$attrs.disabled ? 'cursor-default' : 'cursor-pointer'" @click="toggleDropdown">
      <template v-if="value !== null && ((multiple && value?.length) || !multiple)">
        <span
            v-for="option,i in (multiple ? value : [value])"
            :key="`option-${getId}-${i}`"
            class="flex items-center gap-1"
            :class="{
            'bg-bhav-orange-100 text-bhav-orange-500 rounded-lg p-1 inline-block': color === 'orange',
            'bg-bhav-bright-blue-100 text-bhav-bright-blue-500 rounded-lg p-1 inline-block': color === 'blue'
          }"
        >
          <span>{{ options.find(newOption => newOption.value === option)?.label ?? option }}</span>
          <button
              @click.stop="removeOption(option)"
              class="hover:opacity-70 transition-opacity w-3 h-3 flex items-center justify-center"
              :class="color === 'orange' ? 'text-bhav-orange-600' : 'text-bhav-bright-blue-600'"
              type="button"
          >
            <span class="fa fa-times text-[8px] leading-none"></span>
          </button>
        </span>
      </template>

      <span
          v-else
          class="rounded-lg p-1 block w-full"
          :class="this.$attrs.disabled ? 'text-bhav-blue-100' : 'text-bhav-blue-200 hover:bg-bhav-cool-gray-50'"
      >
        {{ label }}
      </span>
    </p>

    <div
        ref="dropdownRef"
        class="rounded-lg bg-bhav-cool-gray-50 p-1 absolute z-10 overflow-y-auto"
        :style="dropdownStyle"
        :class="[
        minListWidth,
        options.length > 0 && open === id
          ? 'max-w-screen opacity-100 bg-bhav-cool-gray-50 pointer-events-auto'
          : 'max-w-0 max-h-0 opacity-0 bg-transparent overflow-hidden pointer-events-none',

        // Original position logic commented:
        // (collision.y > collision.my ? 'bottom-8' : 'top-8')

        dropdownPositionClass,
        (collision.x > collision.mx ? 'right-0' : 'left-0')
      ]"
    >

      <div v-if="showSearch">
        <SearchInput 
          :placeholder="searchLabel"
          v-model="search.text"
          />
      </div>

      <div class="flex flex-col gap-2 bg-white rounded-lg p-2">
        <p v-if="instructions" class="text-xs text-bhav-blue-200 px-1">{{ instructions }}</p>

        <ul class="flex flex-col gap-1">
          <li
              v-for="option,i in filtered"
              :key="`option-${getId}-${i}`"
              class="text-bhav-blue-200 text-sm"
          >
            <input
                :id="`option-${id}-${i}`"
                :type="multiple ? 'checkbox' : 'radio'"
                v-model="value"
                :value="option.value"
                class="sr-only"
            />
            <label
                :for="`option-${id}-${i}`"
                class="px-2 py-1 hover:bg-bhav-cool-gray-50 rounded-lg cursor-pointer block"
            >
              <span
                  :class="{
                  'bg-bhav-orange-100 text-bhav-orange-500 rounded-lg p-1 inline-block': color === 'orange',
                  'bg-bhav-bright-blue-100 text-bhav-bright-blue-500 rounded-lg p-1 inline-block': color === 'blue'
                }"
              >
                {{ option?.label }}
              </span>
            </label>
          </li>

          <li
              v-if="shouldShowCreate"
              class="text-bhav-blue-200 text-sm px-2 py-1 hover:bg-bhav-cool-gray-50 rounded-lg cursor-pointer block"
              @click="$emit('create', search.text)"
          >
            Create
            <span
                :class="{
                'bg-bhav-orange-100 text-bhav-orange-500 rounded-lg p-1 inline-block': color === 'orange',
                'bg-bhav-bright-blue-100 text-bhav-bright-blue-500 rounded-lg p-1 inline-block': color === 'blue'
              }"
            >
              {{ search.text }}
            </span>
          </li>
        </ul>
      </div>

    </div>
  </div>
</template>

<script>
import { useFormsStore } from '@/stores/forms'
import { useId } from 'vue';
import TextInput from './TextInput.vue';
import SearchInput from './SearchInput.vue';

export default {
  setup() {
    return { id: useId() }
  },

  data() {
    return {
      collision: { x: 0, y: 0, mx: .55, my: .55 },
      search: { text: null },
      dropdownHeight: null,
      dropdownDirectionComputed: 'down'
    }
  },

  emits: ['update:modelValue', 'create'],

  props: {
    modelValue: { required: true },
    label: { type: String, default: "Select" },
    focus: { type: Boolean, default: false },
    options: { type: Array, default: () => [] },
    allowNull: { type: Boolean, default: true },
    empty: { type: String, default: "None Selected..." },
    instructions: { type: String, default: null },
    color: { type: String, default: null },
    multiple: { type: Boolean, default: false },
    searchLabel: { type: String, default: "Search for Keywords" },
    showSearch: { type: Boolean, default: true },
    showCreate: { type: Boolean, default: false },
    containerSelector: { type: String, default: null },
    minListWidth: { type: String, default: 'min-w-64' },

    // New forced direction option
    dropdownDirection: {
      type: String, // 'up', 'down', 'auto'
      default: 'auto'
    },

    // New forced max-height option
    maxDropdownHeight: {
      type: [Number, String],
      default: null
    }
  },

  computed: {
    open: {
      get() { return useFormsStore().open },
      set(v) { useFormsStore().setOpen(v) }
    },

    value: {
      get() { return this.modelValue },
      set(v) { this.$emit('update:modelValue', v) }
    },

    filtered() {
      let options = this.options;
      if (this.search.text) {
        const searchTrimmed = this.search.text.trim();
        options = options.filter(o =>
            JSON.stringify(o).toLowerCase().includes(searchTrimmed.toLowerCase())
        );
      }
      return options;
    },

    shouldShowCreate() {
      if (!this.showCreate || !this.search.text) return false;

      // Check if search text matches any existing option label exactly (case-insensitive)
      const searchLower = this.search.text.toLowerCase().trim();
      const existingMatch = this.options.some(option =>
        option.label?.toLowerCase().trim() === searchLower
      );

      return !existingMatch;
    },

    dropdownStyle() {
      // If user forces a max height
      if (this.maxDropdownHeight !== null) {
        return {
          maxHeight:
              typeof this.maxDropdownHeight === 'number'
                  ? this.maxDropdownHeight + 'px'
                  : this.maxDropdownHeight,
          overflowY: 'auto'
        };
      }

      // Original logic commented
      // return this.dropdownHeight
      //   ? { maxHeight: this.dropdownHeight + 'px', overflowY: 'auto' }
      //   : {};

      return this.dropdownHeight
          ? { maxHeight: this.dropdownHeight + 'px', overflowY: 'auto' }
          : {};
    },

    dropdownPositionClass() {
      return this.dropdownDirectionComputed === 'up'
          ? 'bottom-8'
          : 'top-8';
    },

    getId() {
      return this.$attrs.id || `field-${this.$.uid}`;
    }
  },

  methods: {
    toggleDropdown(e) {
      if (this.$attrs.disabled) return;
      this.avoidCollisions(e);

      const willOpen = this.open !== this.id;
      this.open = willOpen ? this.id : null;

      if (willOpen) {
        this.$nextTick(() => {
          this.determineDropdownDirection();
          this.updateDropdownHeight();
        });
      }
    },

    determineDropdownDirection() {
      if (this.dropdownDirection !== 'auto') {
        this.dropdownDirectionComputed = this.dropdownDirection;
        return;
      }

      const trigger = this.$refs[this.getId];
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        this.dropdownDirectionComputed = 'up';
      } else {
        this.dropdownDirectionComputed = 'down';
      }
    },

    updateDropdownHeight() {
      const dropdown = this.$refs.dropdownRef;
      if (!dropdown) return;

      const r = dropdown.getBoundingClientRect();
      let container = null;

      if (this.containerSelector) {
        container = document.querySelector(this.containerSelector);
      }

      let available;

      if (container) {
        const c = container.getBoundingClientRect();
        available =
            this.dropdownDirectionComputed === 'down'
                ? c.bottom - r.top - 10
                : r.bottom - c.top - 10;
      } else {
        available =
            this.dropdownDirectionComputed === 'down'
                ? window.innerHeight - r.top - 16
                : r.bottom - 16;
      }

      let h = Math.max(120, Math.abs(available));

      // forced max height override
      if (this.maxDropdownHeight !== null) {
        const forced =
            typeof this.maxDropdownHeight === 'number'
                ? this.maxDropdownHeight
                : parseInt(this.maxDropdownHeight);

        h = Math.min(h, forced);
      }

      this.dropdownHeight = h;
    },

    selectOption(v) {
      this.value = v;
      this.closeDropdown();
    },

    removeOption(v) {
      if (this.multiple) {
        this.value = this.value.filter(x => x !== v);
      } else {
        this.value = null;
      }
    },

    closeDropdown() {
      this.open = null;
    },

    handleClickOutside(event) {
      if (
          this.open === this.id &&
          this.$refs[this.getId] &&
          !this.$refs[this.getId].contains(event.target)
      ) {
        this.closeDropdown();
      }
    },

    avoidCollisions(e) {
      if (this.open !== null) return;
      const r = e.target.getBoundingClientRect();
      this.collision.x = r.left / window.innerWidth;
      this.collision.y = r.bottom / window.innerHeight;
    }
  },

  mounted() {
    this.$nextTick(() => document.addEventListener('click', this.handleClickOutside));
    window.addEventListener('resize', this.updateDropdownHeight);

    if (this.focus && this.$refs[this.getId]) {
      this.$refs[this.getId].focus();
    }
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.updateDropdownHeight);
  },

  watch: {
    open(val) {
      if (val === this.id) {
        this.$nextTick(() => {
          this.determineDropdownDirection();
          this.updateDropdownHeight();
        });
      }
    },

    value(newVal, oldVal) {
      if (!this.multiple && newVal !== oldVal) {
        this.closeDropdown();
      }
    }
  },

  components: { TextInput, SearchInput }
}
</script>
